import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { agrogestao } from "@/api/agrogestaoClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FaturasAdmin } from "@/pages/admin/components/FaturasAdmin";
import { LicencasAdmin } from "@/pages/admin/components/LicencasAdmin";
import { PropriedadesAdmin } from "@/pages/admin/components/PropriedadesAdmin";
import { ProdutoresAdmin } from "@/pages/admin/components/ProdutoresAdmin";
import { LogsAdmin } from "@/pages/admin/components/LogsAdmin";
import { ContasReceberAdmin } from "@/pages/admin/components/ContasReceberAdmin";
import { TelemetriaAdmin } from "@/pages/admin/components/TelemetriaAdmin";
import { SuporteAdmin } from "@/pages/admin/components/SuporteAdmin";
import { ConfigAdmin } from "@/pages/admin/components/ConfigAdmin";
import { FinanceiroAdmin } from "@/pages/admin/FinanceiroAdmin";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CreditCard, Users, Building2, Settings, FileText, Wallet, TicketPercent, LogOut, Tractor, ChevronRight } from "lucide-react";
import GraficoDrillDown from "@/components/financeiro/GraficoDrillDown";
import ExportacaoRelatorios from "@/components/financeiro/ExportacaoRelatorios";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { AuditoriaAdmin } from "@/pages/admin/components/AuditoriaAdmin";
import { InternosAdmin } from "@/pages/admin/components/InternosAdmin";
import { ContratosAdmin } from "@/pages/admin/components/ContratosAdmin";

export default function Admin() {
  const [refresh, setRefresh] = useState(0);
  const [tab, setTab] = useState('overview');
  const [selectedPropId, setSelectedPropId] = useState("");

  const { data: user } = useQuery({ queryKey: ['me'], queryFn: () => agrogestao.auth.me() });
  const { data: userRec } = useQuery({
    queryKey: ['user_rec', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const { data } = await agrogestao.client.from('users').select('email, role').eq('email', user.email).maybeSingle();
      return data || null;
    },
    enabled: !!user?.email
  });
  const isAdmin = !!(userRec?.role === 'admin' || /@aguiatecnologia\.com\.br$/i.test(user?.email || '') || /@agrogestao\.com\.br$/i.test(user?.email || ''));
  const isStaff = !!(/@aguiatecnologia\.com\.br$/i.test(user?.email || '') || /@agrogestao\.com\.br$/i.test(user?.email || '') || /softwarehouse/i.test(user?.email || ''));

  const { data: propriedades = [], isLoading: loadingProps } = useQuery({
    queryKey: ["propriedades", refresh],
    queryFn: () => agrogestao.entities.Propriedade.filter({}, "-id")
  });

  const { data: licencas = [], isLoading: loadingLic } = useQuery({
    queryKey: ["licencas", refresh],
    queryFn: async () => {
      try {
        const { data, error } = await agrogestao.client.from('licencas').select('*').order('valid_until', { ascending: false }).limit(2000);
        return error ? [] : (data || []);
      } catch { return []; }
    }
  });

  const { data: faturasAll = [] } = useQuery({
    queryKey: ['faturas_summary', refresh],
    queryFn: async () => {
      try {
        const { data, error } = await agrogestao.client.from('faturas').select('id, licenca_id, status, valor_centavos, created_date, paga_em, vencimento, metodo_pagamento, pix_qrcode_base64, pix_brcode, descricao').order('created_date', { ascending: false }).limit(500);
        return error ? [] : (data || []);
      } catch { return []; }
    },
    enabled: !!isAdmin && (tab === 'financeiro' || tab === 'relatorios' || tab === 'overview')
  });

  const { data: logs = [], isLoading: loadingLogs } = useQuery({
    queryKey: ['audit_logs', refresh],
    queryFn: async () => {
      try {
        const { data, error } = await agrogestao.client.from('audit_logs').select('created_at, user_email, propriedade_id, acao, table, status, details').order('created_at', { ascending: false }).limit(200);
        return error ? [] : (data || []);
      } catch { return []; }
    },
    enabled: !!isAdmin && (tab === 'logs' || tab === 'overview')
  });

  const { data: produtores = [] } = useQuery({
    queryKey: ['produtores', refresh, isAdmin],
    queryFn: async () => {
      if (!isAdmin) return [];
      const { data } = await agrogestao.client.from('users').select('email, role, full_name, created_date').order('created_date', { ascending: false });
      return data || [];
    },
    enabled: !!isAdmin
  });

  const mrrPorMes = useMemo(() => {
    const map = {};
    (licencas || []).filter(l => l.source === 'pix' && l.paid_at && Number(l.amount_cents || 0) > 0).forEach(l => {
      const d = new Date(l.paid_at);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      map[k] = (map[k] || 0) + Number(l.amount_cents || 0);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0])).slice(-12).map(([mes, valor]) => ({ mes, valor: valor / 100 }));
  }, [licencas]);

  const stats = useMemo(() => {
    const ativos = licencas.filter(l => l.status === 'ativo').length;
    const suspensos = licencas.filter(l => l.status === 'bloqueado' || l.status === 'suspenso').length;
    const cancelados = licencas.filter(l => l.status === 'cancelado').length;

    const ativosMensais = licencas.filter(l => l.plano === 'mensal' && l.status === 'ativo');
    const mrr = ativosMensais.reduce((acc, l) => acc + Number(l.amount_cents || 0), 0);

    const today = new Date().toISOString().split('T')[0];
    const expiramHoje = licencas.filter(l => l.status === 'ativo' && String(l.valid_until).startsWith(today)).length;

    const now = new Date();
    const in7 = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const prestesVencer = licencas.filter(l => l.status === 'ativo' && l.valid_until && new Date(l.valid_until) > now && new Date(l.valid_until) <= in7).length;

    const pixFalha = faturasAll.filter(f => f.metodo_pagamento === 'pix' && f.status === 'falha').length;

    return { ativos, suspensos, cancelados, mrr, expiramHoje, prestesVencer, pixFalha };
  }, [licencas, faturasAll]);

  const tabs = [
    { id: 'overview', label: 'Visão Geral', icon: Wallet },
    { id: 'licencas', label: 'Licenças', icon: TicketPercent },
    { id: 'propriedades', label: 'Propriedades', icon: Building2 },
    { id: 'produtores', label: 'Produtores', icon: Users },
    { id: 'financeiro', label: 'Financeiro', icon: CreditCard },
    { id: 'faturas', label: 'Faturas', icon: FileText },
    { id: 'contasReceber', label: 'Contas a Receber', icon: FileText },
    { id: 'telemetria', label: 'Telemetria', icon: FileText },
    { id: 'logs', label: 'Logs', icon: FileText },
    { id: 'suporte', label: 'Suporte', icon: FileText },
    { id: 'config', label: 'Configurações', icon: Settings },
    { id: 'auditoriaAdmin', label: 'Auditoria', icon: FileText },
    { id: 'internos', label: 'Internos', icon: Users },
    { id: 'contratos', label: 'Contratos', icon: FileText }
  ];

  const handleLogout = async () => {
    try {
      await agrogestao.auth.signOut();
      window.location.href = "/";
    } catch (err) {
      console.error("Erro ao sair:", err);
    }
  };

  if (loadingProps || loadingLic || loadingLogs) return <div className="min-h-[60vh] flex items-center justify-center">Carregando...</div>;

  return (
    <div className="grid grid-cols-12 gap-6 p-4 min-h-[calc(100vh-2rem)]">
      {/* Sidebar Enriquecida */}
      <aside className="col-span-3 lg:col-span-2 bg-[#1B4332] text-white rounded-2xl flex flex-col h-[calc(100vh-2rem)] sticky top-4 shadow-xl overflow-hidden border border-white/5">
        {/* Branding/Logo Area */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4A373] rounded-xl flex items-center justify-center shadow-lg">
              <Tractor className="w-6 h-6 text-white" />
            </div>
            <div className="hidden lg:block">
              <h2 className="font-bold text-lg leading-none">Admin</h2>
              <p className="text-[10px] text-white/50 uppercase tracking-widest mt-1">Management</p>
            </div>
          </div>
        </div>

        {/* Navigation Area */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 custom-scrollbar-minimal">
          <div className="space-y-1">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 group
                  ${tab === t.id 
                    ? 'bg-[#D4A373] text-white shadow-lg shadow-[#D4A373]/30' 
                    : 'text-white/70 hover:bg-white/10 hover:text-white'}
                `}
              >
                {t.icon && <t.icon className={`w-4 h-4 transition-transform duration-300 ${tab === t.id ? 'scale-110' : 'group-hover:scale-110'}`} />}
                <span className="flex-1 text-left">{t.label}</span>
                {tab === t.id && <ChevronRight className="w-3 h-3 text-white/50" />}
              </button>
            ))}
          </div>
        </nav>

        {/* User & Logout Area */}
        <div className="p-4 border-t border-white/10 bg-black/10">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 px-2">
              <div className="w-8 h-8 bg-[#40916C] rounded-full flex items-center justify-center text-[10px] font-bold shadow-inner">
                {user?.email?.[0]?.toUpperCase() || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate">{user?.full_name || "Administrador"}</p>
                <p className="text-[10px] text-white/40 truncate">{user?.email}</p>
              </div>
            </div>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="w-full justify-start text-white/60 hover:text-white hover:bg-red-500/20 group h-9 rounded-lg"
            >
              <LogOut className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" />
              <span className="text-xs">Sair do Painel</span>
            </Button>
          </div>
        </div>
      </aside>

      <main className="col-span-9 lg:col-span-10 space-y-6">
        <Card className="border-stone-200 shadow-sm">
          <CardHeader className="border-b bg-stone-50/50 pb-3">
            <CardTitle>{tabs.find(t => t.id === tab)?.label || 'Admin'}</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {tab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-[#1B4332] text-amber-100"><CardHeader className="pb-2"><CardTitle className="text-sm">MRR</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{(stats.mrr / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</CardContent></Card>
                  <Card className="bg-[#0F172A] text-amber-100"><CardHeader className="pb-2"><CardTitle className="text-sm">ARR</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{((stats.mrr * 12) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</CardContent></Card>
                  <Card className="bg-[#2D6A4F] text-amber-100"><CardHeader className="pb-2"><CardTitle className="text-sm">Clientes Ativos</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{stats.ativos}</CardContent></Card>
                  <Card className="bg-[#78350F] text-amber-100"><CardHeader className="pb-2"><CardTitle className="text-sm">Inadimplência</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{faturasAll.length > 0 ? ((faturasAll.filter(f => f.status === 'pendente' || f.status === 'expirado').length / faturasAll.length) * 100).toFixed(1) : 0}%</CardContent></Card>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader><CardTitle className="text-sm">Crescimento MRR (12 meses)</CardTitle></CardHeader>
                    <CardContent>
                      <div className="h-60">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={mrrPorMes}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="mes" fontSize={11} />
                            <YAxis fontSize={11} tickFormatter={(v) => `R$${v}`} />
                            <Tooltip />
                            <Line type="monotone" dataKey="valor" stroke="#166534" strokeWidth={2} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader><CardTitle className="text-sm">Alertas do Sistema</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                      {stats.pixFalha > 0 && <div className="text-red-600 bg-red-50 p-2 rounded text-sm font-medium">⚠️ {stats.pixFalha} pagamentos PIX com falha</div>}
                      {stats.expiramHoje > 0 && <div className="text-amber-600 bg-amber-50 p-2 rounded text-sm font-medium">⚠️ {stats.expiramHoje} licenças expiram hoje</div>}
                      {stats.prestesVencer > 0 && <div className="text-orange-600 bg-orange-50 p-2 rounded text-sm font-medium">⚠️ {stats.prestesVencer} licenças vencem em 7 dias</div>}
                      {!stats.pixFalha && !stats.expiramHoje && !stats.prestesVencer && <div className="text-green-600 bg-green-50 p-2 rounded text-sm">Sistema operando normalmente.</div>}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {tab === 'licencas' && <LicencasAdmin selectedPropId={selectedPropId} refresh={refresh} />}
            {tab === 'propriedades' && <PropriedadesAdmin selectedPropId={selectedPropId} setTab={setTab} setSelectedPropId={setSelectedPropId} refresh={refresh} />}
            {tab === 'produtores' && <ProdutoresAdmin />}
            {tab === 'financeiro' && <FinanceiroAdmin selectedPropId={selectedPropId} />}
            {tab === 'faturas' && <FaturasAdmin selectedPropId={selectedPropId} refresh={refresh} onRefresh={() => setRefresh(x => x + 1)} />}
            {tab === 'contasReceber' && <ContasReceberAdmin />}
            {tab === 'telemetria' && <TelemetriaAdmin />}
            {tab === 'logs' && <LogsAdmin logs={logs} propriedades={propriedades} usersByEmail={{}} />}
            {tab === 'suporte' && <SuporteAdmin />}
            {tab === 'config' && <ConfigAdmin propriedades={propriedades} />}

            {/* Legacy/External components */}
            {tab === 'auditoriaAdmin' && <AuditoriaAdmin />}
            {tab === 'internos' && <InternosAdmin isStaff={isStaff} isAdmin={isAdmin} />}
            {tab === 'contratos' && <ContratosAdmin />}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
